interface NodeLike {
  type: string;
  data: Record<string, unknown>;
  parentId?: string | null;
}

export type ParentLabelResolver = (parentId: string) => string | null;

export function resolveNodeAriaLabel(
  node: NodeLike,
  parentLabel?: ParentLabelResolver,
): string {
  const { type, data } = node;
  let content: string | undefined;
  if (type === 'sticky' && typeof data.text === 'string') content = data.text;
  else if (type === 'text' && typeof data.text === 'string') content = data.text;
  else if (type === 'frame' && typeof data.title === 'string') content = data.title;
  else if (type === 'shape') {
    // Shape nodes carry the variant in `shapeKind` (production) or `shape`
    // (legacy test fixtures); accept either so consumers don't have to
    // duplicate the resolver logic.
    if (typeof data.shapeKind === 'string') content = data.shapeKind;
    else if (typeof data.shape === 'string') content = data.shape;
  }
  else if (type === 'entity-proxy') {
    // Entity proxies carry the displayed name in (a) `entityName` (legacy
    // test fixture / synthetic call site) or (b) the actual production data
    // shape with `cachedTitle` / `virtualTitle`. Accept whichever is present
    // and non-empty so renderers can compute the displayed title once and
    // hand it through.
    if (typeof data.entityName === 'string') content = data.entityName;
    else if (typeof data.cachedTitle === 'string' && data.cachedTitle.length > 0) content = data.cachedTitle;
    else if (typeof data.virtualTitle === 'string' && data.virtualTitle.length > 0) content = data.virtualTitle;
  }
  // freedraw and any unknown types: no content

  const base = content && content.length > 0 ? `${type}: ${content}` : type;

  // Parent-frame discoverability (M4 carryover): suffix "(in <parent title>)"
  // when a child sits inside a frame. Guards a frame-deleted-mid-render race
  // (client.md §6) by requiring the resolver to actually return a label.
  if (node.parentId != null && parentLabel) {
    const parentTitle = parentLabel(node.parentId);
    if (parentTitle && parentTitle.length > 0) {
      return `${base} (in ${parentTitle})`;
    }
  }

  return base;
}
